// KPI figures (revamp PR 5): a value with its label, in a grid. Replaces the gradient tiles and coloured figures;
// decorative colour is retired (decision #15). Text is always in the fg tokens; a tone only colours the icon chip
// (or a dot, without an icon), and only when the figure means a state.
//
//   <StatGrid cols={4}>                                   // tiles, each its own raised card
//     <StatTile label={t('…totalUsers')} value={formatCount(n)} icon={<Users />} />
//     <StatTile label={t('…failed')} value={formatCount(f)} tone="err" hint={t('…sinceMonday')} />
//   </StatGrid>
//   <StatGrid cols={3} variant="inline">                  // inside a card (a ChartCard footer): no frames
//     <StatTile label={t('…peakDay')} value={formatCount(peak)} size="sm" />
//   </StatGrid>
//
// The grid is a `<dl>`, each tile a group of a `<dt>` label and `<dd>`s (icon, value, hint), so keep StatTile inside a
// StatGrid. Values are JetBrains Mono (decision #4: figures in mono). `cols` is the widest layout; narrow screens drop
// to 1–3 columns.
import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';

import { cn } from '@/lib/utils';

export type StatTone = 'neutral' | 'info' | 'ok' | 'warn' | 'err';

const CHIP: Record<StatTone, string> = {
  neutral: 'bg-surface-2 text-fg-2',
  info: 'bg-label/10 text-label',
  ok: 'bg-ok/10 text-ok',
  warn: 'bg-warn/12 text-warn',
  err: 'bg-err/10 text-err',
};

const DOT: Record<StatTone, string> = {
  neutral: 'bg-fg-muted',
  info: 'bg-label',
  ok: 'bg-ok',
  warn: 'bg-warn',
  err: 'bg-err',
};

const COLS = {
  2: 'grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-3',
  4: 'grid-cols-2 lg:grid-cols-4',
  5: 'grid-cols-2 md:grid-cols-3 xl:grid-cols-5',
} as const;

type Variant = 'tiles' | 'inline';
const VariantContext = createContext<Variant>('tiles');

export interface StatGridProps {
  cols?: keyof typeof COLS;
  /** `tiles` (default): each tile is a raised card. `inline`: no frames, for inside a card. */
  variant?: Variant;
  className?: string;
  children: ReactNode;
}

export function StatGrid({ cols = 4, variant = 'tiles', className, children }: StatGridProps) {
  return (
    <VariantContext.Provider value={variant}>
      <dl data-slot="stat-grid" className={cn('grid gap-4', COLS[cols], className)}>
        {children}
      </dl>
    </VariantContext.Provider>
  );
}

export interface StatTileProps {
  label: ReactNode;
  /** The figure, already formatted (`formatCount`, `formatRp`, …). */
  value: ReactNode;
  /** A line under the label: a period, a comparison. */
  hint?: ReactNode;
  /** A lucide icon element, shown in a chip. */
  icon?: ReactNode;
  /** Only for a figure that means a state. Colours the chip (or a dot), never the text. */
  tone?: StatTone;
  /** `md` (default) for KPI rows, `sm` for figures inside a card. */
  size?: 'sm' | 'md';
  className?: string;
}

export function StatTile({ label, value, hint, icon, tone = 'neutral', size = 'md', className }: StatTileProps) {
  const variant = useContext(VariantContext);
  return (
    <div
      data-slot="stat-tile"
      data-tone={tone}
      className={cn(
        'flex min-w-0 flex-col gap-1',
        variant === 'tiles' && 'rounded-card border border-line bg-raised p-4',
        className
      )}
    >
      {/* A <dl> group holds only <dt> and <dd>: the label comes first, `order` shows the icon, then the value, above it. */}
      <dt className="order-3 flex min-w-0 items-center gap-1.5 text-13 text-fg-muted">
        {!icon && tone !== 'neutral' && (
          <span aria-hidden className={cn('size-1.5 shrink-0 rounded-full', DOT[tone])} />
        )}
        <span className="truncate">{label}</span>
      </dt>
      {/* The icon's <dd> has no box (`contents`), so the chip stays a flex item of the tile, first by `order`. */}
      {icon && (
        <dd aria-hidden className="contents">
          <span
            className={cn('order-1 mb-2 grid size-8 place-items-center rounded-control [&>svg]:size-4', CHIP[tone])}
          >
            {icon}
          </span>
        </dd>
      )}
      <dd
        className={cn(
          'order-2 min-w-0 truncate font-mono font-semibold text-fg leading-tight',
          size === 'md' ? 'text-2xl' : 'text-xl'
        )}
      >
        {value}
      </dd>
      {hint && <dd className="order-4 text-fg-muted text-xs">{hint}</dd>}
    </div>
  );
}
